import React, { useMemo, useCallback, useState, useEffect } from 'react';
import ReactFlow, { MiniMap, Controls, Background, Node, Edge, useNodesState, useEdgesState, ReactFlowProvider, useReactFlow } from 'reactflow';
import 'reactflow/dist/style.css';
import { Department } from '@/pages/Departments';
import dagre from 'dagre';
import ELK from 'elkjs/lib/elk.bundled.js';

type LayoutAlgorithm = 'dagre' | 'elk-layered' | 'elk-mrtree';

interface DepartmentOrgChartProps {
    departments: Department[];
}

const nodeWidth = 200;
const nodeHeight = 70;

const getDagreLayoutedElements = (nodes: Node[], edges: Edge[], spacing: number) => {
    const dagreGraph = new dagre.graphlib.Graph();
    dagreGraph.setDefaultEdgeLabel(() => ({}));
    dagreGraph.setGraph({ rankdir: 'TB', nodesep: spacing, ranksep: 120 });

    nodes.forEach((node) => dagreGraph.setNode(node.id, { width: nodeWidth, height: nodeHeight }));
    edges.forEach((edge) => dagreGraph.setEdge(edge.source, edge.target));

    dagre.layout(dagreGraph);

    const layoutedNodes = nodes.map((node) => {
        const nodeWithPosition = dagreGraph.node(node.id);
        return {
            ...node,
            position: { x: nodeWithPosition.x - nodeWidth / 2, y: nodeWithPosition.y - nodeHeight / 2 },
        };
    });

    return { nodes: layoutedNodes, edges };
};

const elk = new ELK();
const getElkLayoutedElements = async (nodes: Node[], edges: Edge[], algorithm: 'layered' | 'mrtree', spacing: number): Promise<{ nodes: Node[], edges: Edge[] }> => {
    const elkEdges = edges.map(edge => ({
        id: edge.id,
        sources: [edge.source],
        targets: [edge.target],
    }));

    const graph: any = {
        id: 'root',
        layoutOptions: {
            'elk.algorithm': algorithm,
            'elk.direction': 'DOWN',
            'elk.layered.spacing.nodeNodeBetweenLayers': `${100 + spacing * 2}`,
            'elk.spacing.nodeNode': `${20 + spacing}`,
            'elk.layered.spacing.edgeNodeBetweenLayers': '80'
        },
        children: nodes.map(node => ({ ...node, width: nodeWidth, height: nodeHeight })),
        edges: elkEdges,
    };

    const layoutedGraph = await elk.layout(graph);

    // Create a map for quick lookup of original node data
    const nodeMap = new Map(nodes.map(node => [node.id, node]));

    const layoutedNodes = layoutedGraph.children?.map((node: any) => {
        const originalNode = nodeMap.get(node.id);
        return {
            ...originalNode, // Spread original node to keep all its properties like `data`
            id: node.id,
            position: { x: node.x, y: node.y },
        };
    }) || [];

    // Filter out any nodes that might have been nullified in the process
    const finalNodes = layoutedNodes.filter(Boolean) as Node[];

    return { nodes: finalNodes, edges };
};

// Helper to build a map of parentId -> children[]
const buildChildrenMap = (departments: Department[]): Map<string, Department[]> => {
    const childrenMap = new Map<string, Department[]>();
    departments.forEach(dept => {
        if (dept.parentId) {
            const children = childrenMap.get(dept.parentId) || [];
            children.push(dept);
            childrenMap.set(dept.parentId, children);
        }
    });
    return childrenMap;
};

// Helper to get all descendants of a node
const getAllDescendantIds = (departmentId: string, childrenMap: Map<string, Department[]>): string[] => {
    const descendants: string[] = [];
    const queue: string[] = [departmentId];
    const visited = new Set<string>();

    while (queue.length > 0) {
        const currentId = queue.shift()!;
        if (visited.has(currentId)) continue;
        visited.add(currentId);

        const children = childrenMap.get(currentId) || [];
        children.forEach(child => {
            descendants.push(child.id);
            queue.push(child.id);
        });
    }
    return descendants;
};

const LayoutedOrgChart: React.FC<DepartmentOrgChartProps> = ({ departments }) => {
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges, onEdgesChange] = useEdgesState([]);
    const { fitView } = useReactFlow();
    const [layoutAlgorithm, setLayoutAlgorithm] = useState<LayoutAlgorithm>('dagre');
    const [spacing, setSpacing] = useState(50);
    const [collapsedNodeIds, setCollapsedNodeIds] = useState<Set<string>>(new Set());

    const handleToggleCollapse = useCallback((departmentId: string) => {
        setCollapsedNodeIds(prev => {
            const newSet = new Set(prev);
            if (newSet.has(departmentId)) {
                newSet.delete(departmentId);
            } else {
                newSet.add(departmentId);
            }
            return newSet;
        });
    }, []);

    const { initialNodes, initialEdges } = useMemo(() => {
        const childrenMap = buildChildrenMap(departments);
        const hiddenNodeIds = new Set<string>();

        collapsedNodeIds.forEach(id => {
            const descendantIds = getAllDescendantIds(id, childrenMap);
            descendantIds.forEach(descId => hiddenNodeIds.add(descId));
        });

        const visibleDepts = departments.filter(dept => !hiddenNodeIds.has(dept.id));

        const isNodeDraggable = false;

        const nodes = visibleDepts.map(dept => {
            const isCollapsed = collapsedNodeIds.has(dept.id);
            const isExpandable = (childrenMap.get(dept.id) || []).length > 0;
            return {
                id: dept.id,
                type: 'default',
                draggable: isNodeDraggable,
                data: {
                    label: (
                        <div style={{ textAlign: 'center', position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '4px' }}>{dept.name}</div>
                            <div style={{ fontSize: '0.8em', color: '#555' }}>{dept.responsibleName || 'N/A'}</div>
                            {isExpandable && (
                                <button
                                    onClick={(e) => { e.stopPropagation(); handleToggleCollapse(dept.id); }}
                                    style={{
                                        position: 'absolute',
                                        top: '-8px',
                                        right: '-8px',
                                        background: '#4f46e5',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '50%',
                                        width: '20px',
                                        height: '20px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        cursor: 'pointer',
                                        lineHeight: '1',
                                        padding: 0,
                                        fontSize: '12px',
                                        fontWeight: 'bold',
                                        zIndex: 10
                                    }}
                                >
                                    {isCollapsed ? '+' : '-'}
                                </button>
                            )}
                        </div>
                    )
                },
                position: { x: 0, y: 0 },
                style: {
                    width: nodeWidth,
                    height: nodeHeight,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }
            };
        });

        const edges = visibleDepts
            .filter(dept => dept.parentId && !hiddenNodeIds.has(dept.parentId))
            .map(dept => ({
                id: `e-${dept.parentId}-${dept.id}`,
                source: dept.parentId!,
                target: dept.id,
                style: { stroke: '#6b7280' },
            }));

        return { initialNodes: nodes, initialEdges: edges };

    }, [departments, collapsedNodeIds, handleToggleCollapse]);

    useEffect(() => {
        const applyLayout = async () => {
            if (initialNodes.length === 0) {
                setNodes([]);
                setEdges([]);
                return;
            }
            let layoutedElements;
            if (layoutAlgorithm === 'dagre') {
                layoutedElements = getDagreLayoutedElements(initialNodes, initialEdges, spacing);
            } else if (layoutAlgorithm === 'elk-layered') {
                layoutedElements = await getElkLayoutedElements(initialNodes, initialEdges, 'layered', spacing);
            } else { // elk-mrtree
                layoutedElements = await getElkLayoutedElements(initialNodes, initialEdges, 'mrtree', spacing);
            }
            const { nodes: layoutedNodes, edges: layoutedEdges } = layoutedElements;
            setNodes(layoutedNodes);
            setEdges(layoutedEdges);

            window.requestAnimationFrame(() => {
                fitView({ duration: 600, padding: 0.1 });
            });
        };
        applyLayout();

    }, [initialNodes, initialEdges, layoutAlgorithm, spacing, fitView, setNodes, setEdges]);

    return (
        <>
            <div className="absolute top-4 left-4 z-10 p-2 bg-gray-200 rounded-md shadow-md flex flex-col space-y-2">
                <div className="flex items-center space-x-1">
                    <button
                        onClick={() => setLayoutAlgorithm('dagre')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${layoutAlgorithm === 'dagre' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-gray-300'}`}
                    >
                        Compacto
                    </button>
                    <button
                        onClick={() => setLayoutAlgorithm('elk-layered')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${layoutAlgorithm === 'elk-layered' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-gray-300'}`}
                    >
                        Vertical
                    </button>
                    <button
                        onClick={() => setLayoutAlgorithm('elk-mrtree')}
                        className={`px-3 py-1 text-sm font-medium rounded-md transition-colors ${layoutAlgorithm === 'elk-mrtree' ? 'bg-white text-gray-800 shadow-sm' : 'bg-transparent text-gray-600 hover:bg-gray-300'}`}
                    >
                        Árvore
                    </button>
                </div>
                <div className="flex items-center space-x-2 px-1">
                    <label htmlFor="spacing" className="text-xs font-medium text-gray-700">Espaço</label>
                    <input
                        type="range"
                        id="spacing"
                        min="0"
                        max="200"
                        value={spacing}
                        onChange={(e) => setSpacing(Number(e.target.value))}
                        className="w-full"
                    />
                </div>
            </div>
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChange}
                onEdgesChange={onEdgesChange}
                fitView
            >
                <Controls />
                <MiniMap />
                <Background gap={12} size={1} />
            </ReactFlow>
        </>
    );
};

const DepartmentOrgChart: React.FC<DepartmentOrgChartProps> = (props) => (
    <div style={{ height: '70vh', width: '100%' }} className="bg-gray-50 rounded-lg relative">
        <ReactFlowProvider>
            <LayoutedOrgChart {...props} />
        </ReactFlowProvider>
    </div>
);

export default DepartmentOrgChart; 